import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatNumberString } from '../../../../utility/utility';
import { adLicenseClickEvent } from '../../../../services/analyticsService';
import { PAGE_SECTIONS, INTERACTED_FROM } from '../../../../services/analyticsService/constants';
import { useGetRegaAdLicensesSummaryQuery } from '../../apis/regaAdLicenses';
import { getCardState, redirectToVerifyFal } from './utils';
import { SkeletonBlock } from './styled';
import {
  ClearCard,
  UnverifiedCard,
  PendingCard,
  CountBlock,
  ActiveLabel,
  TickCircle,
  ClearContent,
  Content,
  Title,
  ClearSubtitle,
  Subtitle,
  BlurredCount,
  Count,
  LockRow,
  ActionLink,
} from './ad-license-backlog-card.styled';
import { ChevronRightIcon, LockIcon, TickIcon } from './icons';
import AdLicensesListModal from './ad-licenses-list-modal';

const AdLicenseBacklogCard = ({ onPostLicense = () => {} }) => {
  const { t } = useTranslation();
  const { user: loginUser } = useSelector((state) => state.app.loginUser);
  const [listOpen, setListOpen] = useState(false);

  const { data: summary, isLoading } = useGetRegaAdLicensesSummaryQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );

  if (isLoading) {
    return <SkeletonBlock height="70px" radius="6px" />;
  }
  // No ticket copy exists for an error state, and the manual flow must stay usable — render nothing.
  if (!summary) return null;

  const state = getCardState(summary);

  const openList = () => {
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.VIEW_ALL });
    setListOpen(true);
  };

  const goVerifyFal = () => {
    adLicenseClickEvent(loginUser, { value: 'fal_unverified', interactedFrom: INTERACTED_FROM.POST_LISTING });
    redirectToVerifyFal();
  };

  if (state === 'clear') {
    return (
      <ClearCard>
        <TickCircle>
          <TickIcon size={15} />
        </TickCircle>
        <ClearContent>
          <Title>{t('All Ad Licenses Posted')}</Title>
          <ClearSubtitle>{t("You're all caught up.")}</ClearSubtitle>
        </ClearContent>
      </ClearCard>
    );
  }

  if (state === 'unverified') {
    return (
      <UnverifiedCard type="button" onClick={goVerifyFal}>
        <CountBlock>
          <BlurredCount>
            ##
          </BlurredCount>
          <ActiveLabel>{t('ACTIVE')}</ActiveLabel>
        </CountBlock>
        <Content>
          <LockRow>
            <LockIcon size={15} />
            <Title>
              {t('Verify your FAL to see active ad licenses')}
            </Title>
          </LockRow>
          <Subtitle>
            {t('You can still post by entering a license number above.')}
          </Subtitle>
        </Content>
        <ActionLink>
          {t('Verify', { context: 'adLicense' })}
          <ChevronRightIcon size={14} />
        </ActionLink>
      </UnverifiedCard>
    );
  }

  // pending
  return (
    <>
      <PendingCard onClick={openList} role="button" tabIndex={0}>
        <CountBlock>
          <Count>
            {formatNumberString(summary.unposted_count)}
          </Count>
          <ActiveLabel>{t('ACTIVE')}</ActiveLabel>
        </CountBlock>
        <Content>
          <Title>
            {t('Ad licenses not posted')}
          </Title>
          <Subtitle>
            {t('Browse the full list and pick one to post.')}
          </Subtitle>
        </Content>
        <ActionLink>
          {t('View all')}
          <ChevronRightIcon size={14} />
        </ActionLink>
      </PendingCard>
      <AdLicensesListModal
        visible={listOpen}
        onClose={() => setListOpen(false)}
        onPostLicense={onPostLicense}
        summary={summary}
      />
    </>
  );
};

export default AdLicenseBacklogCard;
